import { useCallback, useEffect, useState } from 'react';
import { AlertTriangle, Check, Download, Printer, RefreshCw, ShieldAlert } from 'lucide-react';
import { ComoCambiarVersionCaja } from '@renderer/components/settings/ComoCambiarVersionCaja';
import { CajasConectadas } from '@renderer/components/settings/CajasConectadas';
import type { CodigoEmparejamientoVista, EstadoImpresionVista } from '@shared/types';
import { useSessionStore } from '@renderer/store/useSessionStore';
import { traducirErrorBackend } from '@renderer/lib/mensajesError';

/**
 * Sprint 59 — Instalación y estado de la impresión automática.
 *
 * Esta pantalla existe por una razón de negocio: el soporte está en otra ciudad.
 * Si instalar el agente requiere una visita o una sesión remota asistida, el
 * producto no se puede vender a un segundo cliente. Así que el dueño tiene que
 * poder hacerlo solo, leyendo.
 *
 * Dos decisiones de diseño que sostienen todo lo demás:
 *
 *  1. **Se anticipa la advertencia de Windows.** El ejecutable no está firmado
 *     (decisión de negocio del 2026-08-03), así que SmartScreen dirá "Editor
 *     desconocido". Si el operador se topa con esa pantalla azul sin aviso, se
 *     detiene y llama por teléfono. Aquí se le dibuja ANTES, con los dos clics
 *     exactos, y explicando por qué aparece. Anticipar el susto es la diferencia
 *     entre una instalación de tres minutos y una llamada.
 *
 *  2. **El paso 3 se marca solo.** El estado viene del servidor: cuando la caja
 *     conecta de verdad, el paso se pone en verde sin que nadie toque nada. El
 *     operador no tiene que interpretar si funcionó — lo ve.
 */

interface VersionDisponible {
  disponible: boolean;
  version: string | null;
  tamanoMb: number | null;
}

export function AgenteImpresionSection({ onConectado }: { onConectado?: (v: boolean) => void }) {
  const [estado, setEstado] = useState<EstadoImpresionVista | null>(null);
  const [descarga, setDescarga] = useState<VersionDisponible | null>(null);
  /*
    Sprint 74 — El tipo viene del contrato (`CodigoEmparejamientoVista`) en vez de reescribirse aquí.
    Reescrito a mano se quedó sin `traeNegocio` cuando el Sprint 73 lo agregó, y la nota que le dice
    a la dueña "cópialo completo, con el nombre del negocio adelante" leía un campo que para
    TypeScript no existía. Ahora el compilador avisa en vez de callarse.
  */
  const [codigo, setCodigo] = useState<CodigoEmparejamientoVista | null>(null);
  const [generando, setGenerando] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const esDueno = useSessionStore((s) => s.sesion?.rol) === 'dueño';

  const cargar = useCallback(async () => {
    const res = await window.api.impresion.estado();
    if (res.ok) {
      setEstado(res.data);
      onConectado?.(res.data.agentes.some((a) => a.conectado));
    }
    try {
      const r = await fetch('/api/agente/version');
      if (r.ok) setDescarga((await r.json()) as VersionDisponible);
    } catch {
      /* sin dato de versión se oculta el botón de descarga, no se rompe la pantalla */
    }
  }, [onConectado]);

  useEffect(() => {
    void cargar();
    // Sondeo lento: lo justo para que el paso 3 se marque solo mientras el
    // operador tiene la pantalla abierta instalando.
    const id = window.setInterval(() => void cargar(), 5_000);
    return () => window.clearInterval(id);
  }, [cargar]);

  const generarCodigo = async () => {
    setGenerando(true);
    setError(null);
    const res = await window.api.impresion.codigoEmparejamiento();
    setGenerando(false);
    if (res.ok) setCodigo(res.data);
    else setError(traducirErrorBackend(res.error, res.codigo));
  };

  /**
   * Un reloj que avanza, sólo mientras hay un código en pantalla.
   *
   * 2026-08-05: el código traía "Vence a las 19:58" y nada más. La dueña lo generó,
   * se fue a descargar e instalar el programa, y cuando volvió el código estaba
   * vencido — sin manera de saberlo desde esta pantalla y, peor, sin el botón para
   * generar otro, porque desaparecía en cuanto había uno.
   */
  const [ahora, setAhora] = useState(() => Date.now());
  useEffect(() => {
    if (!codigo) return;
    const id = window.setInterval(() => setAhora(Date.now()), 1_000);
    return () => window.clearInterval(id);
  }, [codigo]);

  const msRestantes = codigo ? new Date(codigo.expira).getTime() - ahora : 0;
  const vencido = codigo !== null && msRestantes <= 0;
  /** "18 horas", "45 minutos", "2 minutos": en la unidad que importa al leerlo. */
  const tiempoRestante = (() => {
    const min = Math.floor(msRestantes / 60_000);
    if (min >= 120) return `${Math.floor(min / 60)} horas`;
    if (min >= 1) return `${min} ${min === 1 ? 'minuto' : 'minutos'}`;
    return 'menos de un minuto';
  })();

  const agentes = estado?.agentes ?? [];
  const conectado = agentes.some((a) => a.conectado);
  const hayCaja = agentes.length > 0;

  // ── Ya funciona: se dice en una línea y se quita el instructivo de en medio ──
  if (conectado) {
    return (
      <div className="rounded-xl border-2 border-green-300 bg-green-50 p-4">
        <div className="flex items-start gap-3">
          <span className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-green-600">
            <Check className="h-5 w-5 text-white" />
          </span>
          <div className="flex-1">
            <p className="font-semibold text-green-900">Impresión automática funcionando</p>
            <p className="mt-0.5 text-sm text-green-900">
              Los tickets salen solos, del tamaño exacto y cortados. Ya no aparece la ventana de
              imprimir.
            </p>
            <ul className="mt-2 space-y-0.5 text-sm text-green-900">
              {agentes
                .filter((a) => a.conectado)
                .map((a) => (
                  <li key={a.nombre}>
                    <span className="font-medium">{a.nombre}</span>
                    {a.versionAgente && <span className="text-green-700"> · versión {a.versionAgente}</span>}
                  </li>
                ))}
            </ul>
            {(estado?.fallidos ?? 0) > 0 && (
              <p className="mt-2 rounded-md bg-red-100 px-3 py-2 text-sm font-medium text-red-900">
                {estado?.fallidos} ticket(s) no se pudieron imprimir. Revisa que la impresora tenga
                papel y esté encendida.
              </p>
            )}
            {/*
              Sprint 74 — El bloque trae su propio botón de descarga, y por eso necesita el peso del
              instalador. Los pasos del asistente (donde vivía el único botón) no se dibujan en esta
              rama: si la caja está conectada, el asistente no existe.
            */}
            <ComoCambiarVersionCaja
              estado={estado}
              versionNueva={descarga?.version ?? null}
              tamanoMb={descarga?.tamanoMb ?? null}
            />
            {esDueno && <CajasConectadas repartidas={estado?.repartidas === true} />}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {/* ── Encabezado: qué se gana. Sin esto, nadie instala nada ── */}
      <div className="rounded-xl border border-slate-200 bg-white p-4">
        <div className="flex items-start gap-3">
          <Printer className="mt-0.5 h-6 w-6 shrink-0 text-primary" />
          <div>
            <h3 className="font-semibold">Impresión automática (recomendado)</h3>
            <p className="mt-1 text-sm text-slate-600">
              Hoy, cada vez que cobras, se abre la ventana de imprimir y el ticket sale más largo
              de lo necesario. Con el programa de la caja instalado:
            </p>
            <ul className="mt-2 space-y-1 text-sm text-slate-700">
              <li>• El ticket sale solo, sin ventana ni botones.</li>
              <li>• Del tamaño justo y cortado, sin desperdiciar papel.</li>
              <li>• No hay que configurar márgenes ni tamaño de hoja nunca más.</li>
            </ul>
            <p className="mt-2 text-sm text-slate-600">
              Se instala <span className="font-medium">una sola vez</span> en la computadora que
              tiene la impresora conectada. Después se actualiza sola.
            </p>
          </div>
        </div>
      </div>

      {hayCaja && !conectado && <CajaSinResponder estado={estado} />}
      {/* Sprint 156 — También aquí: una caja que no responde puede ser justo la que sobra. */}
      {esDueno && hayCaja && <CajasConectadas repartidas={estado?.repartidas === true} />}

      {!esDueno ? (
        <p className="rounded-lg bg-slate-100 px-4 py-3 text-sm text-slate-700">
          Pídele al dueño que instale la impresión automática desde esta pantalla.
        </p>
      ) : (
        <>
          <Paso
            numero={1}
            titulo="Descarga el programa"
            hecho={false}
            detalle={
              descarga?.disponible ? (
                <>
                  <p className="text-sm text-slate-600">
                    Guárdalo en el Escritorio para encontrarlo fácil.
                  </p>
                  <a
                    href="/api/agente/instalador"
                    className="btn-primary mt-3 inline-flex min-h-[44px] items-center gap-2"
                  >
                    <Download className="h-5 w-5" />
                    Descargar {descarga.tamanoMb ? `(${descarga.tamanoMb} MB)` : ''}
                  </a>
                  {descarga.version && (
                    <p className="mt-1 text-xs text-slate-500">Versión {descarga.version}</p>
                  )}
                </>
              ) : (
                <p className="rounded-md bg-amber-50 px-3 py-2 text-sm text-amber-900">
                  Todavía no hay programa publicado. Avísale a soporte.
                </p>
              )
            }
          />

          <Paso
            numero={2}
            titulo="Instálalo (Windows te va a avisar, es normal)"
            hecho={false}
            detalle={<AvisoWindows />}
          />

          <Paso
            numero={3}
            titulo="Conecta la caja con este código"
            hecho={conectado}
            detalle={
              <>
                <p className="text-sm text-slate-600">
                  Al terminar de instalar, el programa se abre solo y pide un código. Genera uno
                  aquí y escríbelo ahí.
                </p>
                {codigo && (
                  <div
                    className={`mt-3 rounded-xl border-2 px-4 py-3 ${
                      vencido ? 'border-amber-300 bg-amber-50' : 'border-primary/40 bg-primary/5'
                    }`}
                  >
                    <p
                      className={`font-mono text-4xl font-bold tracking-[0.2em] ${
                        vencido ? 'text-slate-400 line-through' : 'text-slate-900'
                      }`}
                    >
                      {codigo.codigo}
                    </p>
                    {vencido ? (
                      <p className="mt-1 text-sm font-medium text-amber-900">
                        Este código ya venció. Genera otro aquí abajo y escríbelo en el programa
                        de la caja.
                      </p>
                    ) : (
                      <p className="mt-1 text-sm text-slate-600">
                        Sirve una sola vez y le queda{' '}
                        <span className="font-semibold">{tiempoRestante}</span> (vence a las{' '}
                        {new Date(codigo.expira).toLocaleTimeString('es-MX', {
                          hour: '2-digit',
                          minute: '2-digit',
                        })}
                        ). Tómate tu tiempo para instalar.
                      </p>
                    )}
                    {/*
                      Sprint 73 — Decir que el código ya trae la dirección adentro.

                      La dueña nunca pudo conectar la caja porque el agente le pedía además la
                      dirección del sistema, con un ejemplo en gris que ella leyó como un valor ya
                      puesto. Ahora el código la lleva, y eso hay que decirlo aquí: es donde ella
                      está mirando cuando va a copiarlo.
                    */}
                    {!vencido && codigo.traeNegocio && (
                      <p className="mt-2 rounded bg-white/70 px-2 py-1 text-xs text-slate-700">
                        Cópialo <b>completo, con el nombre del negocio adelante</b>. Es lo único que
                        el programa de la caja necesita: la dirección la saca de ahí.
                      </p>
                    )}
                  </div>
                )}
                {/*
                  El botón NO desaparece al generar: si el código vence o se pierde la
                  nota donde lo apuntó, tiene que poder sacar otro sin recargar nada.
                */}
                <button
                  onClick={generarCodigo}
                  disabled={generando}
                  className="btn-secondary mt-3 min-h-[44px]"
                >
                  {generando
                    ? 'Generando…'
                    : codigo
                      ? 'Generar otro código'
                      : hayCaja
                        ? 'Generar código para otra caja'
                        : 'Generar mi código'}
                </button>
                {error && <p className="mt-2 text-sm text-red-700">{error}</p>}
                <p className="mt-3 flex items-center gap-2 text-sm text-slate-500">
                  <RefreshCw className="h-4 w-4 animate-spin" />
                  Esta pantalla se marca sola en verde cuando la caja quede conectada.
                </p>
              </>
            }
          />
        </>
      )}
    </div>
  );
}

/**
 * Anticipa la pantalla de SmartScreen.
 *
 * El ejecutable no va firmado, así que este aviso NO es opcional: es la diferencia
 * entre que el dueño complete la instalación o que se detenga asustado. Se dibuja
 * el diálogo real para que reconozca lo que va a ver, y se marcan los dos clics.
 */
function AvisoWindows() {
  return (
    <>
      <ol className="space-y-1 text-sm text-slate-700">
        <li>1. Abre el archivo que descargaste (doble clic).</li>
        <li>
          2. Aparecerá una <span className="font-medium">pantalla azul de Windows</span> como ésta:
        </li>
      </ol>

      {/* Representación del diálogo, para que lo reconozca cuando lo vea */}
      <div className="mt-2 overflow-hidden rounded-lg border border-slate-300 shadow-sm">
        <div className="bg-[#0067b8] px-4 py-3 text-white">
          <p className="flex items-center gap-2 text-sm font-semibold">
            <ShieldAlert className="h-4 w-4" />
            Windows protegió tu PC
          </p>
          <p className="mt-1 text-xs opacity-90">
            Microsoft Defender SmartScreen impidió el inicio de una aplicación no reconocida.
          </p>
          <p className="mt-2 text-xs font-semibold underline decoration-dotted">Más información</p>
          <span className="mt-1 inline-block rounded bg-yellow-300 px-1.5 py-0.5 text-[11px] font-bold text-yellow-950">
            ← toca aquí primero
          </span>
        </div>
        <div className="flex items-center justify-between gap-2 bg-slate-100 px-4 py-2">
          <span className="rounded bg-white px-3 py-1 text-xs text-slate-500 shadow-sm">
            Ejecutar de todas formas
          </span>
          <span className="rounded bg-yellow-300 px-1.5 py-0.5 text-[11px] font-bold text-yellow-950">
            ← y después aquí
          </span>
        </div>
      </div>

      <div className="mt-3 rounded-lg bg-slate-100 px-3 py-2 text-sm text-slate-700">
        <p className="font-medium">¿Por qué sale ese aviso?</p>
        <p className="mt-0.5">
          Porque el programa es nuestro y no se descarga de una tienda de aplicaciones. Windows
          avisa de todos los programas que no conoce todavía. No es un virus.
        </p>
      </div>

      <ol className="mt-3 space-y-1 text-sm text-slate-700" start={3}>
        <li>3. Sigue la instalación con “Siguiente” hasta el final.</li>
        <li>4. Al terminar, el programa se abre solo y te pide el código del paso 3.</li>
      </ol>

      {/*
        Sprint 73 — El estorbo más común al reinstalar, dicho antes de que ocurra.

        Si el programa está corriendo —y siempre lo está, vive junto al reloj— el instalador se
        queda a medias o avisa que el archivo está en uso, y nadie relaciona eso con el íconito de
        la esquina. Decirlo aquí cuesta dos renglones; descubrirlo cuesta una llamada.
      */}
      <div className="mt-3 rounded-md bg-slate-50 px-3 py-2 text-sm text-slate-700">
        <p className="font-medium">¿Ya tenías el programa instalado?</p>
        <p className="mt-0.5">
          Ciérralo antes de instalar: clic derecho en el ícono de la impresora que está{' '}
          <b>junto al reloj</b> → <b>Salir</b>. Si el instalador dice que el archivo está en uso, es
          por eso.
        </p>
      </div>
    </>
  );
}

/** Un paso del asistente. `hecho` lo decide el servidor, no el operador. */
function Paso({
  numero,
  titulo,
  hecho,
  detalle,
}: {
  numero: number;
  titulo: string;
  hecho: boolean;
  detalle: React.ReactNode;
}) {
  return (
    <div
      className={`rounded-xl border p-4 ${
        hecho ? 'border-green-300 bg-green-50' : 'border-slate-200 bg-white'
      }`}
    >
      <div className="flex items-start gap-3">
        <span
          className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-base font-bold ${
            hecho ? 'bg-green-600 text-white' : 'bg-primary/10 text-primary'
          }`}
        >
          {hecho ? <Check className="h-5 w-5" /> : numero}
        </span>
        <div className="min-w-0 flex-1">
          <h4 className={`font-semibold ${hecho ? 'text-green-900' : 'text-slate-900'}`}>{titulo}</h4>
          <div className="mt-1">{detalle}</div>
        </div>
      </div>
    </div>
  );
}

/** La caja existe pero no responde: qué revisar, en orden de probabilidad. */
function CajaSinResponder({ estado }: { estado: EstadoImpresionVista | null }) {
  const caja = estado?.agentes[0];
  return (
    <div className="rounded-xl border-2 border-amber-300 bg-amber-50 p-4">
      <div className="flex items-start gap-3">
        <AlertTriangle className="mt-0.5 h-6 w-6 shrink-0 text-amber-700" />
        <div>
          <p className="font-semibold text-amber-900">
            La caja tiene el programa instalado, pero no responde
          </p>
          <p className="mt-1 text-sm text-amber-900">
            Mientras tanto los tickets se imprimen como antes, con la ventana de imprimir. Revisa
            en la computadora de la caja:
          </p>
          <ul className="mt-2 space-y-1 text-sm text-amber-900">
            <li>• Que esté encendida y con internet.</li>
            <li>
              • Que el programa <span className="font-medium">RestaurantPOS Impresión</span> esté
              abierto (busca su icono junto al reloj, abajo a la derecha).
            </li>
            <li>• Si no aparece, ábrelo desde el menú de inicio.</li>
          </ul>
          {caja?.ultimoContacto && (
            <p className="mt-2 text-xs text-amber-800">
              Última señal:{' '}
              {new Date(caja.ultimoContacto).toLocaleString('es-MX', {
                day: '2-digit',
                month: '2-digit',
                hour: '2-digit',
                minute: '2-digit',
              })}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

/**
 * Aviso de versión atrasada. Informativo a propósito: el agente se actualiza solo,
 * así que no hay ninguna acción que pedirle al operador.
 */
/*
  Sprint 73 — Aquí vivía `VersionAtrasada`, que decía: "la caja se actualizará sola la próxima vez
  que se cierre el programa; no tienes que hacer nada".

  Era **falso** para la versión que el cliente tenía instalada: ese paquete salió sin
  `electron-updater` adentro (hotfix v1.62.1), así que no podía actualizarse por su cuenta. La dueña
  habría esperado por algo que nunca iba a pasar — y eso es peor que no decir nada.

  Lo reemplaza `ComoCambiarVersionCaja`, que distingue los dos casos y, cuando hay que hacer algo,
  lo explica paso por paso.
*/
